import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Download, FileSpreadsheet, Loader2, Search, SlidersHorizontal, Sparkles, X } from 'lucide-react';
import { useMemo, useState, type ComponentProps, type ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { DocumentTypeLabel, StatusBadge } from '@/components/app/status-badge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NlDateInput } from '@/components/ui/nl-date-input';
import { Select } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/lib/api';
import { downloadZip, exportXlsx, type SelectedDocSummary } from '@/lib/bulk-actions';
import { askDocuments } from '@/lib/mutations';
import { DOCUMENT_STATUSES, DOCUMENT_TYPES } from '@/lib/labels';
import { formatAmount, formatDateISO } from '@/lib/utils';

interface Search {
	entity_code?: string;
	status?: string;
	document_type?: string;
	direction?: string;
	needs_review?: string;
	from_date?: string;
	to_date?: string;
	q?: string;
	tag?: string;
	limit?: number;
	offset?: number;
}

export const Route = createFileRoute('/documents/')({
	component: DocumentsList,
	validateSearch: (search: Record<string, unknown>): Search => ({
		entity_code: typeof search.entity_code === 'string' ? search.entity_code : undefined,
		status: typeof search.status === 'string' ? search.status : undefined,
		document_type: typeof search.document_type === 'string' ? search.document_type : undefined,
		direction: typeof search.direction === 'string' ? search.direction : undefined,
		needs_review: typeof search.needs_review === 'string' ? search.needs_review : undefined,
		from_date: typeof search.from_date === 'string' ? search.from_date : undefined,
		to_date: typeof search.to_date === 'string' ? search.to_date : undefined,
		q: typeof search.q === 'string' ? search.q : undefined,
		tag: typeof search.tag === 'string' ? search.tag : undefined,
		limit: typeof search.limit === 'number' ? search.limit : undefined,
		offset: typeof search.offset === 'number' ? search.offset : undefined,
	}),
});

function DocumentsList() {
	const search = Route.useSearch();
	const navigate = useNavigate({ from: Route.fullPath });
	const [qInput, setQInput] = useState(search.q ?? '');
	const [showFilters, setShowFilters] = useState(false);
	const [showAsk, setShowAsk] = useState(false);
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [bulkAction, setBulkAction] = useState<null | 'zip' | 'xlsx'>(null);
	const [bulkError, setBulkError] = useState<string | null>(null);

	const toggleSelect = (id: string) =>
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});

	const clearSelection = () => setSelectedIds(new Set());

	const entitiesQuery = useQuery({ queryKey: ['entities'], queryFn: () => api.entities() });
	const documentsQuery = useQuery({
		queryKey: ['documents', search],
		queryFn: () =>
			api.documents({
				entity_code: search.entity_code,
				status: search.status,
				document_type: search.document_type,
				direction: search.direction,
				needs_review: search.needs_review,
				from_date: search.from_date,
				to_date: search.to_date,
				q: search.q,
				tag: search.tag,
				limit: search.limit ?? 50,
				offset: search.offset ?? 0,
			}),
	});

	const tagsQuery = useQuery({ queryKey: ['document-tags'], queryFn: () => api.documentTags() });

	const selectedSummaries: SelectedDocSummary[] = useMemo(() => {
		const items = documentsQuery.data?.items ?? [];
		return items
			.filter((d) => selectedIds.has(d.id))
			.map((d) => ({
				id: d.id,
				original_filename: d.original_filename,
				counterparty_name: d.counterparty_name,
				document_date: d.document_date,
			}));
	}, [documentsQuery.data, selectedIds]);

	const runZip = async () => {
		setBulkAction('zip');
		setBulkError(null);
		try {
			await downloadZip(selectedSummaries);
		} catch (err) {
			setBulkError(err instanceof Error ? err.message : String(err));
		} finally {
			setBulkAction(null);
		}
	};

	const runXlsx = async () => {
		setBulkAction('xlsx');
		setBulkError(null);
		try {
			await exportXlsx(selectedSummaries.map((s) => s.id));
		} catch (err) {
			setBulkError(err instanceof Error ? err.message : String(err));
		} finally {
			setBulkAction(null);
		}
	};

	const setSearch = (patch: Partial<Search>) => {
		navigate({
			search: (prev) => ({
				...prev,
				...patch,
				offset: patch.offset ?? 0,
			}),
		});
	};

	const clearFilters = () => {
		navigate({ search: () => ({}) });
		setQInput('');
	};

	const hasFilters = Object.values(search).some((v) => v !== undefined && v !== '' && v !== 0);

	return (
		<div className="mx-auto max-w-screen-2xl space-y-6">
			<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
				<div>
					<h1 className="text-2xl font-semibold tracking-tight">Documenten</h1>
					<p className="text-sm text-muted-foreground">
						{documentsQuery.data?.count ?? '…'} document{documentsQuery.data?.count === 1 ? '' : 'en'} gevonden
					</p>
				</div>
				<div className="flex gap-2">
					<Button variant="outline" size="sm" onClick={() => setShowAsk((v) => !v)}>
						<Sparkles className="mr-2 h-4 w-4" />
						{showAsk ? 'Verberg vraag' : 'Vraag aan DocR'}
					</Button>
					<Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}>
						<SlidersHorizontal className="mr-2 h-4 w-4" />
						{showFilters ? 'Verberg filters' : 'Toon filters'}
					</Button>
				</div>
			</div>
			{showAsk && <AskPanel />}

			{/* Quick search */}
			<form
				onSubmit={(e) => {
					e.preventDefault();
					setSearch({ q: qInput || undefined });
				}}
				className="flex items-center gap-2"
			>
				<div className="relative flex-1">
					<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
					<Input
						placeholder="Zoek op tegenpartij, onderwerp..."
						className="pl-9"
						value={qInput}
						onChange={(e) => setQInput(e.target.value)}
					/>
				</div>
				<Button type="submit" size="sm">
					Zoek
				</Button>
				{hasFilters && (
					<Button type="button" variant="ghost" size="sm" onClick={clearFilters}>
						<X className="mr-1.5 h-4 w-4" />
						Wis
					</Button>
				)}
			</form>

			{/* Filter panel */}
			{showFilters && (
				<Card>
					<CardContent className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 lg:grid-cols-4">
						<div className="space-y-1.5">
							<Label>Entiteit</Label>
							<Select
								value={search.entity_code ?? ''}
								onChange={(e) => setSearch({ entity_code: e.target.value || undefined })}
							>
								<option value="">Alle</option>
								{entitiesQuery.data?.entities.map((e) => (
									<option key={e.code} value={e.code}>
										{e.name}
									</option>
								))}
							</Select>
						</div>
						<div className="space-y-1.5">
							<Label>Status</Label>
							<Select
								value={search.status ?? ''}
								onChange={(e) => setSearch({ status: e.target.value || undefined })}
							>
								<option value="">Alle</option>
								{DOCUMENT_STATUSES.map((s) => (
									<option key={s.value} value={s.value}>
										{s.label}
									</option>
								))}
							</Select>
						</div>
						<div className="space-y-1.5">
							<Label>Documenttype</Label>
							<Select
								value={search.document_type ?? ''}
								onChange={(e) => setSearch({ document_type: e.target.value || undefined })}
							>
								<option value="">Alle</option>
								{DOCUMENT_TYPES.map((t) => (
									<option key={t.value} value={t.value}>
										{t.label}
									</option>
								))}
							</Select>
						</div>
						<div className="space-y-1.5">
							<Label>Review nodig</Label>
							<Select
								value={search.needs_review ?? ''}
								onChange={(e) => setSearch({ needs_review: e.target.value || undefined })}
							>
								<option value="">Alle</option>
								<option value="true">Alleen review</option>
								<option value="false">Alleen OK</option>
							</Select>
						</div>
						<div className="space-y-1.5">
							<Label>Datum vanaf</Label>
							<NlDateInput
								value={search.from_date ?? ''}
								onChange={(iso) => setSearch({ from_date: iso || undefined })}
							/>
						</div>
						<div className="space-y-1.5">
							<Label>Datum tot</Label>
							<NlDateInput
								value={search.to_date ?? ''}
								onChange={(iso) => setSearch({ to_date: iso || undefined })}
							/>
						</div>
						<div className="space-y-1.5">
							<Label>Tag</Label>
							<Input
								list="filter-tag-suggestions"
								placeholder="bv. energie"
								value={search.tag ?? ''}
								onChange={(e) => setSearch({ tag: e.target.value || undefined })}
							/>
							<datalist id="filter-tag-suggestions">
								{tagsQuery.data?.tags.map((t) => (
									<option key={t} value={t} />
								))}
							</datalist>
						</div>
					</CardContent>
				</Card>
			)}

			{search.tag && (
				<div className="flex items-center gap-2 text-xs text-muted-foreground">
					<span>Filter op tag:</span>
					<Badge variant="muted" className="gap-1 pr-1">
						{search.tag}
						<button
							type="button"
							onClick={() => setSearch({ tag: undefined })}
							className="rounded-full p-0.5 hover:bg-muted-foreground/10"
							aria-label="Wis tag-filter"
						>
							<X className="h-3 w-3" />
						</button>
					</Badge>
				</div>
			)}

			<Card>
				<CardContent className="p-0">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead className="w-10">
									<input
										type="checkbox"
										aria-label="Selecteer alles op deze pagina"
										checked={
											documentsQuery.data
												? documentsQuery.data.items.length > 0 &&
													documentsQuery.data.items.every((d) => selectedIds.has(d.id))
												: false
										}
										onChange={(e) => {
											if (!documentsQuery.data) return;
											setSelectedIds((prev) => {
												const next = new Set(prev);
												if (e.target.checked) {
													for (const d of documentsQuery.data.items) next.add(d.id);
												} else {
													for (const d of documentsQuery.data.items) next.delete(d.id);
												}
												return next;
											});
										}}
									/>
								</TableHead>
								<TableHead>Datum</TableHead>
								<TableHead>Entiteit</TableHead>
								<TableHead>Type</TableHead>
								<TableHead>Afzender</TableHead>
								<TableHead>Geadresseerde</TableHead>
								<TableHead className="text-right">Bedrag</TableHead>
								<TableHead>Status</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{documentsQuery.isLoading && (
								<TableRow>
									<TableCell colSpan={8} className="h-24 text-center text-sm text-muted-foreground">
										Laden...
									</TableCell>
								</TableRow>
							)}
							{documentsQuery.data?.items.map((doc) => (
								<TableRow
									key={doc.id}
									className={selectedIds.has(doc.id) ? 'bg-muted/40' : ''}
								>
									<TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
										<input
											type="checkbox"
											aria-label={`Selecteer ${doc.original_filename ?? doc.id}`}
											checked={selectedIds.has(doc.id)}
											onChange={() => toggleSelect(doc.id)}
										/>
									</TableCell>
									<TableCell
										className="cursor-pointer whitespace-nowrap font-mono text-xs text-muted-foreground"
										onClick={() => navigate({ to: '/documents/$id', params: { id: doc.id } })}
									>
										{formatDateISO(doc.document_date ?? doc.created_at)}
									</TableCell>
									<TableCell
										className="cursor-pointer whitespace-nowrap"
										onClick={() => navigate({ to: '/documents/$id', params: { id: doc.id } })}
									>
										<Badge variant="muted">{doc.entity_code ?? '—'}</Badge>
									</TableCell>
									<TableCell
										className="cursor-pointer"
										onClick={() => navigate({ to: '/documents/$id', params: { id: doc.id } })}
									>
										<div className="flex flex-col">
											<DocumentTypeLabel type={doc.document_type} />
											{doc.needs_review ? (
												<span className="mt-0.5 text-[10px] uppercase tracking-wide text-amber-700">review</span>
											) : null}
										</div>
									</TableCell>
									<TableCell
										className="max-w-[14rem] cursor-pointer truncate"
										onClick={() => navigate({ to: '/documents/$id', params: { id: doc.id } })}
									>
										{doc.counterparty_name ?? <span className="text-muted-foreground">—</span>}
									</TableCell>
									<TableCell
										className="max-w-[14rem] cursor-pointer truncate text-sm"
										onClick={() => navigate({ to: '/documents/$id', params: { id: doc.id } })}
									>
										{doc.addressee ? (
											doc.addressee.split('\n')[0]
										) : (
											<span className="text-muted-foreground">—</span>
										)}
									</TableCell>
									<TableCell
										className="cursor-pointer whitespace-nowrap text-right font-mono text-xs"
										onClick={() => navigate({ to: '/documents/$id', params: { id: doc.id } })}
									>
										{formatAmount(doc.amount_total, doc.currency ?? 'EUR')}
									</TableCell>
									<TableCell
										className="cursor-pointer"
										onClick={() => navigate({ to: '/documents/$id', params: { id: doc.id } })}
									>
										<StatusBadge status={doc.status} />
									</TableCell>
								</TableRow>
							))}
							{documentsQuery.data && documentsQuery.data.items.length === 0 && (
								<TableRow>
									<TableCell colSpan={8} className="h-24 text-center text-sm text-muted-foreground">
										Geen documenten gevonden met deze filters.
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</CardContent>
			</Card>

			{/* Pagination */}
			{documentsQuery.data && documentsQuery.data.count > (documentsQuery.data.limit ?? 50) && (
				<div className="flex items-center justify-between gap-2 text-sm text-muted-foreground">
					<div>
						{(documentsQuery.data.offset ?? 0) + 1} - {(documentsQuery.data.offset ?? 0) + documentsQuery.data.items.length} van{' '}
						{documentsQuery.data.count}
					</div>
					<div className="flex gap-2">
						<Button
							variant="outline"
							size="sm"
							disabled={(documentsQuery.data.offset ?? 0) === 0}
							onClick={() =>
								setSearch({
									offset: Math.max(0, (documentsQuery.data?.offset ?? 0) - (documentsQuery.data?.limit ?? 50)),
								})
							}
						>
							Vorige
						</Button>
						<Button
							variant="outline"
							size="sm"
							disabled={
								(documentsQuery.data.offset ?? 0) + documentsQuery.data.items.length >= documentsQuery.data.count
							}
							onClick={() =>
								setSearch({
									offset: (documentsQuery.data?.offset ?? 0) + (documentsQuery.data?.limit ?? 50),
								})
							}
						>
							Volgende
						</Button>
					</div>
				</div>
			)}

			{/* Hidden Link import is intentional to keep router route generated when empty state */}
			<span className="hidden">
				<Link to="/">.</Link>
			</span>

			{selectedIds.size > 0 && (
				<div className="fixed inset-x-0 bottom-4 z-20 mx-auto flex max-w-screen-md items-center gap-3 rounded-full border bg-background px-4 py-2 shadow-lg lg:left-60">
					<span className="text-sm font-medium">
						{selectedIds.size} geselecteerd
					</span>
					<div className="ml-auto flex gap-2">
						<Button size="sm" variant="outline" onClick={runZip} disabled={bulkAction !== null}>
							{bulkAction === 'zip' ? (
								<Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
							) : (
								<Download className="mr-1.5 h-4 w-4" />
							)}
							ZIP
						</Button>
						<Button size="sm" variant="outline" onClick={runXlsx} disabled={bulkAction !== null}>
							{bulkAction === 'xlsx' ? (
								<Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
							) : (
								<FileSpreadsheet className="mr-1.5 h-4 w-4" />
							)}
							XLSX
						</Button>
						<Button size="sm" variant="ghost" onClick={clearSelection} disabled={bulkAction !== null}>
							<X className="mr-1.5 h-4 w-4" />
							Wis
						</Button>
					</div>
				</div>
			)}
			{bulkError && (
				<div className="fixed inset-x-0 bottom-20 z-20 mx-auto max-w-screen-md rounded-md border border-destructive/40 bg-destructive/10 px-4 py-2 text-xs text-destructive shadow lg:left-60">
					{bulkError}
				</div>
			)}
		</div>
	);
}

type Citation = {
	tag: string;
	document_id: string;
	counterparty_name: string | null;
	document_date: string | null;
	amount_total: string | null;
	currency: string | null;
	subject: string | null;
};

// Splitst tekst rond [DOCn] markers en geeft per token een React-node terug;
// markers worden vervangen door een klikbare superscript-chip. Werkt binnen
// markdown-elementen (paragraphs, list-items, table-cellen).
function citeTokenize(text: string, citations: Citation[]): ReactNode[] {
	const parts = text.split(/(\[DOC\d+\])/g);
	return parts.map((part, i) => {
		const match = /^\[DOC(\d+)\]$/.exec(part);
		if (!match) return part;
		const idx = Number(match[1]) - 1;
		const cite = citations[idx];
		if (!cite) return part;
		return (
			<Link
				key={`cite-${i}-${cite.document_id}`}
				to="/documents/$id"
				params={{ id: cite.document_id }}
				className="ml-0.5 inline-flex h-4 min-w-[1rem] items-center justify-center rounded bg-primary/10 px-1 align-super font-mono text-[10px] font-semibold text-primary no-underline hover:bg-primary/20"
				title={cite.counterparty_name ?? cite.document_id}
			>
				{idx + 1}
			</Link>
		);
	});
}

function SourcesGrid({ citations }: { citations: Citation[] }) {
	const COLLAPSED_LIMIT = 12;
	const [expanded, setExpanded] = useState(false);
	const visible = expanded ? citations : citations.slice(0, COLLAPSED_LIMIT);
	const hidden = citations.length - visible.length;

	return (
		<div className="space-y-2">
			<div className="flex items-center justify-between">
				<div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
					Bronnen ({citations.length})
				</div>
				{hidden > 0 && !expanded && (
					<button
						type="button"
						onClick={() => setExpanded(true)}
						className="text-[11px] text-primary hover:underline"
					>
						+{hidden} tonen
					</button>
				)}
				{expanded && citations.length > COLLAPSED_LIMIT && (
					<button
						type="button"
						onClick={() => setExpanded(false)}
						className="text-[11px] text-primary hover:underline"
					>
						Inklappen
					</button>
				)}
			</div>
			<div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
				{visible.map((c, i) => (
					<Link
						key={c.tag}
						to="/documents/$id"
						params={{ id: c.document_id }}
						className="group rounded-md border bg-muted/10 p-2 text-xs transition-colors hover:border-primary/40 hover:bg-primary/5"
					>
						<div className="flex items-start gap-2">
							<span className="flex h-5 min-w-[1.25rem] items-center justify-center rounded bg-primary/10 px-1 font-mono text-[10px] font-semibold text-primary">
								{i + 1}
							</span>
							<div className="flex-1 space-y-0.5">
								<div className="font-medium text-foreground group-hover:text-primary">
									{c.counterparty_name ?? '—'}
								</div>
								<div className="flex flex-wrap gap-x-2 text-muted-foreground">
									{c.document_date && <span>{formatDateISO(c.document_date)}</span>}
									{c.amount_total && (
										<span>
											{c.currency ?? 'EUR'} {formatAmount(c.amount_total)}
										</span>
									)}
								</div>
							</div>
						</div>
					</Link>
				))}
			</div>
		</div>
	);
}

function citeChildren(children: ReactNode, citations: Citation[]): ReactNode {
	// react-markdown geeft children als string of array; we walken tekst-knopen
	// en laten de niet-tekst-children onaangetast (zodat <strong>, <em> blijven).
	if (typeof children === 'string') return citeTokenize(children, citations);
	if (Array.isArray(children)) {
		return children.map((c, i) =>
			typeof c === 'string' ? <span key={i}>{citeTokenize(c, citations)}</span> : c,
		);
	}
	return children;
}

function AskPanel() {
	const [question, setQuestion] = useState('');
	const [error, setError] = useState<string | null>(null);
	const mutation = useMutation({
		mutationFn: (q: string) => askDocuments({ question: q }),
		onError: (err) => {
			const body = err && typeof err === 'object' && 'body' in err ? (err as { body: unknown }).body : err;
			setError(typeof body === 'object' ? JSON.stringify(body) : String(body));
		},
		onSuccess: () => setError(null),
	});

	const submit = (e: React.FormEvent) => {
		e.preventDefault();
		const q = question.trim();
		if (q.length < 3) return;
		mutation.mutate(q);
	};

	const citations = mutation.data?.citations ?? [];

	const markdownComponents: ComponentProps<typeof ReactMarkdown>['components'] = {
		p: ({ children }) => <p className="mb-2 last:mb-0">{citeChildren(children, citations)}</p>,
		li: ({ children }) => <li>{citeChildren(children, citations)}</li>,
		strong: ({ children }) => <strong>{citeChildren(children, citations)}</strong>,
		em: ({ children }) => <em>{citeChildren(children, citations)}</em>,
		td: ({ children }) => <td className="border px-2 py-1">{citeChildren(children, citations)}</td>,
		th: ({ children }) => <th className="border bg-muted/30 px-2 py-1 text-left">{children}</th>,
		ul: ({ children }) => <ul className="my-2 ml-5 list-disc space-y-0.5">{children}</ul>,
		ol: ({ children }) => <ol className="my-2 ml-5 list-decimal space-y-0.5">{children}</ol>,
		table: ({ children }) => <table className="my-2 border-collapse text-xs">{children}</table>,
	};

	return (
		<Card>
			<CardContent className="space-y-3 p-4">
				<form onSubmit={submit} className="flex items-center gap-2">
					<Sparkles className="h-4 w-4 text-muted-foreground" />
					<Input
						placeholder='Bijv. "Hoeveel heb ik bij Q data solutions betaald in januari 2025?"'
						value={question}
						onChange={(e) => setQuestion(e.target.value)}
						disabled={mutation.isPending}
					/>
					<Button type="submit" size="sm" disabled={mutation.isPending || question.trim().length < 3}>
						{mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Vraag'}
					</Button>
				</form>
				{error && (
					<div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
						{error}
					</div>
				)}
				{mutation.isPending && (
					<div className="space-y-2 rounded-md border bg-muted/10 p-3">
						<div className="h-3 w-3/4 animate-pulse rounded bg-muted" />
						<div className="h-3 w-2/3 animate-pulse rounded bg-muted" />
						<div className="h-3 w-1/2 animate-pulse rounded bg-muted" />
					</div>
				)}
				{mutation.data && (
					<div className="space-y-4 rounded-md border bg-background p-4">
						{mutation.data.used_filter_path && mutation.data.filters_detected && (
							<div className="rounded-md border border-blue-200 bg-blue-50/40 px-3 py-1.5 text-[11px] text-blue-900">
								Toegepaste filter:{' '}
								{mutation.data.filters_detected.counterparty && (
									<span className="font-medium">{mutation.data.filters_detected.counterparty}</span>
								)}
								{mutation.data.filters_detected.counterparty && mutation.data.filters_detected.date_from && ' · '}
								{mutation.data.filters_detected.date_from && (
									<span>
										{mutation.data.filters_detected.date_from} t/m {mutation.data.filters_detected.date_to}
									</span>
								)}
								{typeof mutation.data.matched_count === 'number' && (
									<span className="text-muted-foreground"> — {mutation.data.matched_count} document{mutation.data.matched_count === 1 ? '' : 'en'} meegenomen</span>
								)}
							</div>
						)}
						<div className="text-sm leading-relaxed">
							<ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
								{mutation.data.answer}
							</ReactMarkdown>
						</div>
						{mutation.data.citations.length > 0 && (
							<SourcesGrid citations={mutation.data.citations} />
						)}
					</div>
				)}
			</CardContent>
		</Card>
	);
}


